import React, { useState, useEffect, useRef, useMemo } from 'react'
import { X, Download } from 'lucide-react'
import ModalTest from '../../../BaseModal'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { ContainerOfModal, Header, HeaderTitle, CloseDiv, Button } from '../../../styles/modal-style'
import TechnicalSheetPDF from '../../../../components/PDF/FashionProduct/TechnicalSheetPDF'
import { ProductData } from '../../../../components/PDF/FashionProduct/technicalSheetPdfTypes'
import {
  SwitchesContainer,
  SwitchRow,
  SwitchLabelText,
  SwitchBox,
  SwitchInput,
  SwitchSlider
} from './style'
import { ApprovalApi } from '../../../../pages/ClientPanel/ProductComposition/ApprovationModal/api'
import axios from 'axios'
import AttachmentApiService from '../../../../services/AttachmentApiService'

export interface SelectedSections {
  imagens: boolean
  cores: boolean
  composicao: boolean
  anotacoes: boolean
  aprovacoes: boolean
  medidas: boolean
}

interface GeneratePDFModalProps {
  onClickClose: () => void
  productId?: number | string
  productData?: ProductData | null
  gradesByType?: any
  gradeTypes?: any[]
  coverImageUrl?: string | null
  attachmentImageUrls?: string[]
  coverScale?: number | null
  coverPosX?: number | null
  coverPosY?: number | null
  extraData?: any
  compositionItems?: any[]
  liveObservations?: string
  liveAnotacoes?: any[]
  liveCores?: any[]
  liveMedidas?: number[]
}

const GeneratePDFModal: React.FC<GeneratePDFModalProps> = ({ onClickClose, productId, productData: initialProductData, gradesByType: initialGradesByType, gradeTypes: initialGradeTypes, coverImageUrl: initialCoverImageUrl, attachmentImageUrls: initialAttachmentImageUrls, coverScale: initialCoverScale, coverPosX: initialCoverPosX, coverPosY: initialCoverPosY, extraData: initialExtraData, compositionItems: initialCompositionItems, liveObservations: initialLiveObservations, liveAnotacoes: initialLiveAnotacoes, liveCores: initialLiveCores, liveMedidas: initialLiveMedidas }) => {
  const [fetchedProduct, setFetchedProduct] = useState<any>(null)
  const [fetchedGradeTypes, setFetchedGradeTypes] = useState<any[]>([])
  const [loadingProduct, setLoadingProduct] = useState(false)
  const [base64Cover, setBase64Cover] = useState<string | null>(null)
  const [base64Images, setBase64Images] = useState<string[]>([])
  const [convertedAnotacoes, setConvertedAnotacoes] = useState<any[]>([])
  const [isConvertingImages, setIsConvertingImages] = useState(false)
  const [liveAprovacoes, setLiveAprovacoes] = useState<any[]>([])
  const [convertedAprovacoes, setConvertedAprovacoes] = useState<any[]>([])
  const [liveMeasures, setLiveMeasures] = useState<any[]>([])

  const targetProductId = initialProductData?.id || (productId ? Number(productId) : null)

  useEffect(() => {
    if (!initialProductData && targetProductId) {
      setLoadingProduct(true)
      const token = localStorage.getItem('token')
      const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
      const headers = { Authorization: `Bearer ${token}` }

      Promise.all([
        axios.get(`${GATEWAY_URL}/backend-fashion-orders/fashion-product/${targetProductId}`, { headers }).catch(err => {
          console.error('Erro ao buscar dados do produto para PDF:', err)
          return null
        }),
        axios.get(`${GATEWAY_URL}/backend-fashion-orders/fashion-product/grades/types`, { headers }).catch(err => {
          console.error('Erro ao buscar grade types para PDF:', err)
          return null
        })
      ]).then(([productRes, gradeTypesRes]) => {
        if (productRes?.data) {
          setFetchedProduct(productRes.data)
        }
        if (gradeTypesRes?.data && Array.isArray(gradeTypesRes.data)) {
          setFetchedGradeTypes(gradeTypesRes.data)
        }
        setLoadingProduct(false)
      })
    }
  }, [targetProductId, initialProductData])

  const productData = initialProductData || fetchedProduct
  const gradeTypes = initialGradeTypes && initialGradeTypes.length > 0 ? initialGradeTypes : fetchedGradeTypes

  const gradesByType = useMemo(() => {
    if (initialGradesByType) return initialGradesByType
    if (!productData) return {}
    const map: Record<string, any[]> = {}
    const gradesList = productData.GradeInProduct || productData.gradeInProduct || []
    gradesList.forEach((gp: any) => {
      const g = gp.Grade || gp.grade || gp
      const typeId = String(g.typeId || g.GraduationType?.id || 'default')
      if (!map[typeId]) map[typeId] = []
      map[typeId].push(g)
    })
    return map
  }, [initialGradesByType, productData])

  const coverImageUrl = useMemo(() => {
    if (initialCoverImageUrl !== undefined) return initialCoverImageUrl
    if (!productData) return null
    const attachments = productData.Attachments || productData.FileInProduct || []
    const coverAtt = attachments.find((a: any) => a.isCover)
    const imageAtt = attachments.find((a: any) => a.isImage || /\.(png|jpe?g|gif|bmp|webp|svg|avif|heic|heif|jfif)$/i.test(a.fileUrl || a.filePath || ''))
    return coverAtt?.fileUrl || coverAtt?.filePath || productData.CoverGeneralFile?.fileUrl || imageAtt?.fileUrl || imageAtt?.filePath || null
  }, [initialCoverImageUrl, productData])

  const attachmentImageUrls = useMemo(() => {
    if (initialAttachmentImageUrls !== undefined) return initialAttachmentImageUrls
    if (!productData) return []
    const attachments = productData.Attachments || productData.FileInProduct || []
    return attachments
      .filter((a: any) => a.isImage || /\.(png|jpe?g|gif|bmp|webp|svg|avif|heic|heif|jfif)$/i.test(a.fileUrl || a.filePath || a.originalName || ''))
      .sort((a: any, b: any) => Number(Boolean(b.isCover)) - Number(Boolean(a.isCover)))
      .map((a: any) => a.fileUrl || a.filePath)
      .filter(Boolean) as string[]
  }, [initialAttachmentImageUrls, productData])

  const coverScale = initialCoverScale !== undefined ? initialCoverScale : ((productData?.Attachments || productData?.FileInProduct || []).find((a: any) => a.isCover)?.coverScale ?? productData?.coverScale ?? productData?.coverImageScale ?? null)
  const coverPosX = initialCoverPosX !== undefined ? initialCoverPosX : ((productData?.Attachments || productData?.FileInProduct || []).find((a: any) => a.isCover)?.coverPosX ?? productData?.coverPosX ?? productData?.coverImagePosX ?? null)
  const coverPosY = initialCoverPosY !== undefined ? initialCoverPosY : ((productData?.Attachments || productData?.FileInProduct || []).find((a: any) => a.isCover)?.coverPosY ?? productData?.coverPosY ?? productData?.coverImagePosY ?? null)

  const compositionItems = useMemo(() => {
    if (initialCompositionItems !== undefined) return initialCompositionItems
    if (!productData?.Script) return []
    return productData.Script.map((s: any) => ({
      id: s.id,
      tipo: s.type || s.tipo || '',
      stockId: s.stockId,
      materialId: s.materialId,
      stageId: s.stageId,
      descricao: s.descricao || s.description || '',
      finishing: s.finishing || s.acabamento || '',
      consumo: s.consumption != null ? String(s.consumption) : (s.consumo || ''),
      unitOfMeasurementId: s.unitOfMeasurementId,
      unit: s.UnitOfMeasurement?.name || s.unit || '',
      cost: s.cost != null ? String(s.cost) : (s.custo || ''),
      sizeRange: s.sizeRange || [],
      colorVariants: s.colorVariants || [],
    }))
  }, [initialCompositionItems, productData])

  const liveObservations = initialLiveObservations !== undefined ? initialLiveObservations : (productData?.observations || '')

  const liveAnotacoes = useMemo(() => {
    if (initialLiveAnotacoes !== undefined) return initialLiveAnotacoes
    if (!productData?.BeneficiamentoNote) return []
    return productData.BeneficiamentoNote.map((n: any) => ({
      id: n.id,
      sectorId: n.sectorId,
      sectorName: n.Sector?.name || n.sectorName || '',
      description: n.description || '',
      imageUrls: (n.FileInBeneficiamentoNote || n.Files || []).map((f: any) => f.GeneralFile?.fileUrl || f.fileUrl).filter(Boolean),
      createdAt: n.createdAt,
      updatedAt: n.updatedAt,
      User: n.User,
    }))
  }, [initialLiveAnotacoes, productData])

  const liveCores = useMemo(() => {
    if (initialLiveCores !== undefined) return initialLiveCores
    const colorsList = productData?.productItemColors || productData?.ProductItemColor || []
    return colorsList.map((pic: any) => {
      const col = pic.CollectionColor || pic.collectionColor || pic
      return {
        id: col.id || pic.id,
        name: col.name || pic.name || '',
        hex: col.hex || col.colorHex || pic.hex || '',
        colorHex: col.colorHex || col.hex || pic.hex || '',
      }
    })
  }, [initialLiveCores, productData])

  const liveMedidas = useMemo(() => {
    if (initialLiveMedidas !== undefined) return initialLiveMedidas
    const gradesList = productData?.GradeInProduct || productData?.gradeInProduct || []
    return gradesList.map((gp: any) => gp.gradeId || gp.Grade?.id || gp.id).filter(Boolean)
  }, [initialLiveMedidas, productData])

  const extraData = useMemo(() => {
    if (initialExtraData !== undefined) return initialExtraData
    if (!productData) return {}
    return {
      price: productData.price,
      collectionName: productData.Collection?.name || 'Nenhuma',
      familyName: productData.Family?.name || 'Indefinida',
      groupName: productData.Group?.name || 'Nenhum',
      episodeName: productData.Episode?.name || (productData.Episode?.number ? `Episódio ${productData.Episode.number}` : 'Nenhum'),
      gradeTypeName: productData.GradeInProduct?.[0]?.Grade?.GraduationType?.name || 'Indefinido',
      supplierCode: productData.supplierCode || 'Não informado',
      clientIdentification: productData.clientIdentification || 'Não informado',
    }
  }, [initialExtraData, productData])

  const userToggledRef = useRef<Record<keyof SelectedSections, boolean>>({
    imagens: false,
    cores: false,
    composicao: false,
    anotacoes: false,
    aprovacoes: false,
    medidas: false,
  })

  const hasImagens = !!coverImageUrl || (attachmentImageUrls || []).length > 0 || !!productData?.CoverGeneralFile?.fileUrl || !!(productData?.Attachments && productData.Attachments.length > 0)
  const hasCores = !!(liveCores && liveCores.length > 0) || !!(productData?.productItemColors && productData.productItemColors.length > 0) || !!(productData?.ProductItemColor && productData.ProductItemColor.length > 0)
  const hasComposicao = !!(compositionItems && compositionItems.length > 0) || !!(productData?.composition?.variations && productData.composition.variations.length > 0) || !!(productData?.Script && productData.Script.length > 0)
  const hasAnotacoes = !!(liveObservations && liveObservations.trim().length > 0) || !!(productData?.observations && productData.observations.trim().length > 0) || !!(liveAnotacoes && liveAnotacoes.length > 0) || !!(productData?.BeneficiamentoNote && productData.BeneficiamentoNote.length > 0)
  const hasAprovacoes = (liveAprovacoes && liveAprovacoes.length > 0) || !!(productData?.ProductApproval && productData.ProductApproval.length > 0)
  const hasMedidas = (liveMeasures && liveMeasures.length > 0) || !!(productData?.ProductMeasure && productData.ProductMeasure.length > 0)

  const [selectedSections, setSelectedSections] = useState<SelectedSections>({
    imagens: Boolean(hasImagens),
    cores: Boolean(hasCores),
    composicao: Boolean(hasComposicao),
    anotacoes: Boolean(hasAnotacoes),
    aprovacoes: Boolean(hasAprovacoes),
    medidas: Boolean(hasMedidas)
  })

  useEffect(() => {
    setSelectedSections(prev => ({
      imagens: hasImagens ? (userToggledRef.current.imagens ? prev.imagens : true) : false,
      cores: hasCores ? (userToggledRef.current.cores ? prev.cores : true) : false,
      composicao: hasComposicao ? (userToggledRef.current.composicao ? prev.composicao : true) : false,
      anotacoes: hasAnotacoes ? (userToggledRef.current.anotacoes ? prev.anotacoes : true) : false,
      aprovacoes: hasAprovacoes ? (userToggledRef.current.aprovacoes ? prev.aprovacoes : true) : false,
      medidas: hasMedidas ? (userToggledRef.current.medidas ? prev.medidas : true) : false,
    }))
  }, [hasImagens, hasCores, hasComposicao, hasAnotacoes, hasAprovacoes, hasMedidas])

  const attachmentImageUrlsKey = (attachmentImageUrls || []).join('|')
  const liveAnotacoesKey = JSON.stringify((liveAnotacoes || []).map((n: any) => ({
    id: n.id,
    urls: n.imageUrls,
    previews: n.imagePreviews,
    orig: n.imageOriginalUrls
  })))
  const liveAprovacoesKey = JSON.stringify((liveAprovacoes || []).map((a: any) => ({
    id: a.id,
    front: a.FrontImage?.fileUrl,
    side: a.SideImage?.fileUrl,
    back: a.BackImage?.fileUrl,
  })))

  useEffect(() => {
    if (targetProductId) {
      ApprovalApi.list(Number(targetProductId))
        .then(async (list) => {
          if (Array.isArray(list) && list.length > 0) {
            const fullDetails = await Promise.all(
              list.map(item => ApprovalApi.getById(item.id).catch(() => item))
            )
            setLiveAprovacoes(fullDetails)
          } else {
            setLiveAprovacoes([])
          }
        })
        .catch(err => console.error('Erro ao buscar aprovações no modal de PDF:', err))
        
      const token = localStorage.getItem('token')
      const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
      axios.get(`${GATEWAY_URL}/backend-fashion-orders/fashion-product/${targetProductId}/measures`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => {
        setLiveMeasures(Array.isArray(res.data) ? res.data : [])
      })
      .catch(err => console.error('Erro ao buscar medidas no modal de PDF:', err))
    }
  }, [targetProductId])

  useEffect(() => {
    const convertImageUrlToDataUrl = async (imageUrl: string): Promise<string | null> => {
      if (!imageUrl) return null
      if (imageUrl.startsWith('data:')) return imageUrl
      if (imageUrl.startsWith('blob:')) {
        try {
          const blob = await fetch(imageUrl).then(res => res.blob())
          return await new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result as string)
            reader.onerror = reject
            reader.readAsDataURL(blob)
          })
        } catch (err) {
          console.error('Erro ao converter imagem local para base64:', err)
          return null
        }
      }
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        try {
          const resp = await fetch(imageUrl)
          if (resp.ok) {
            const blob = await resp.blob()
            return await new Promise<string>((resolve, reject) => {
              const reader = new FileReader()
              reader.onloadend = () => resolve(reader.result as string)
              reader.onerror = reject
              reader.readAsDataURL(blob)
            })
          }
        } catch (err) {
          console.warn('Falha no fetch direto da imagem, tentando via gateway backend:', imageUrl, err)
        }
      }
      try {
        const result = await AttachmentApiService.getBase64(imageUrl);
        const dataUrl = result?.dataUrl;
        return typeof dataUrl === 'string' && dataUrl.startsWith('data:') ? dataUrl : null;
      } catch (err) {
        console.error('Erro ao converter imagem do produto para base64:', imageUrl, err);
        return null;
      }
    }

    let cancelled = false
    setIsConvertingImages(true)

    const runConversion = async () => {
      const orderedUrls = [coverImageUrl, ...(attachmentImageUrls || [])]
        .filter((url): url is string => Boolean(url))
      const uniqueUrls = Array.from(new Set(orderedUrls))

      const [convertedProductResults, notesWithConvertedImages, approvalsWithConvertedImages] = await Promise.all([
        Promise.all(uniqueUrls.map(convertImageUrlToDataUrl)),
        Promise.all(
          (liveAnotacoes || []).map(async (nota: any) => {
            const rawImages = [
              ...(nota.imagePreviews || []),
              ...(nota.imageUrls || []),
              ...(nota.imageOriginalUrls || []),
              ...(nota.Files?.map((f: any) => f.GeneralFile?.fileUrl) || []),
            ].filter((u: any): u is string => typeof u === 'string' && u.length > 0)
            const uniqueNoteUrls = Array.from(new Set(rawImages))

            const convertedNoteImages = (
              await Promise.all(uniqueNoteUrls.map(convertImageUrlToDataUrl))
            ).filter((u): u is string => Boolean(u))

            return {
              ...nota,
              imageUrls: convertedNoteImages.length > 0 ? convertedNoteImages : uniqueNoteUrls,
            }
          })
        ),
        Promise.all(
          (liveAprovacoes || []).map(async (aprov) => {
            const frontUrl = aprov.FrontImage?.fileUrl || aprov.frontImage?.fileUrl || (typeof aprov.FrontImage === 'string' ? aprov.FrontImage : null) || aprov.frontImageUrl
            const sideUrl = aprov.SideImage?.fileUrl || aprov.sideImage?.fileUrl || (typeof aprov.SideImage === 'string' ? aprov.SideImage : null) || aprov.sideImageUrl
            const backUrl = aprov.BackImage?.fileUrl || aprov.backImage?.fileUrl || (typeof aprov.BackImage === 'string' ? aprov.BackImage : null) || aprov.backImageUrl

            const [convertedFront, convertedSide, convertedBack] = await Promise.all([
              frontUrl ? convertImageUrlToDataUrl(frontUrl) : null,
              sideUrl ? convertImageUrlToDataUrl(sideUrl) : null,
              backUrl ? convertImageUrlToDataUrl(backUrl) : null,
            ])

            const effectiveFront = convertedFront || frontUrl || null
            const effectiveSide = convertedSide || sideUrl || null
            const effectiveBack = convertedBack || backUrl || null

            return {
              ...aprov,
              FrontImage: effectiveFront ? { ...aprov.FrontImage, fileUrl: effectiveFront } : null,
              SideImage: effectiveSide ? { ...aprov.SideImage, fileUrl: effectiveSide } : null,
              BackImage: effectiveBack ? { ...aprov.BackImage, fileUrl: effectiveBack } : null,
              frontImageUrl: effectiveFront,
              sideImageUrl: effectiveSide,
              backImageUrl: effectiveBack,
            }
          })
        )
      ])

      if (cancelled) return
      const convertedImages = convertedProductResults.filter((result: any): result is string => Boolean(result))

      setBase64Images(convertedImages)
      if (convertedImages.length > 0) setBase64Cover(convertedImages[0])
      setConvertedAnotacoes(notesWithConvertedImages)
      setConvertedAprovacoes(approvalsWithConvertedImages)
      setIsConvertingImages(false)
    }

    runConversion()

    return () => {
      cancelled = true
    }
  }, [coverImageUrl, attachmentImageUrlsKey, liveAnotacoesKey, liveAprovacoesKey])

  const toggleSection = (key: keyof SelectedSections) => {
    userToggledRef.current[key] = true
    setSelectedSections(prev => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <ModalTest>
      <ContainerOfModal width='450px'>
        <Header>
          <HeaderTitle>Gerar PDF da Ficha Técnica</HeaderTitle>
          <CloseDiv onClick={onClickClose}>
            <X size={20} />
          </CloseDiv>
        </Header>
        
        <div>
          <p style={{ fontSize: '14px', color: '#666', marginTop: '1rem' }}>
            Selecione as seções que deseja incluir no documento PDF:
          </p>

          <SwitchesContainer>
            <SwitchRow style={{ opacity: hasImagens ? 1 : 0.5, cursor: hasImagens ? 'pointer' : 'not-allowed' }}>
              <SwitchLabelText style={{ cursor: 'inherit' }}>Imagens</SwitchLabelText>
              <SwitchBox style={{ cursor: 'inherit' }}>
                <SwitchInput 
                  type="checkbox" 
                  checked={hasImagens && selectedSections.imagens} 
                  onChange={() => hasImagens && toggleSection('imagens')} 
                  disabled={!hasImagens}
                />
                <SwitchSlider className="slider" />
              </SwitchBox>
            </SwitchRow>

            <SwitchRow style={{ opacity: hasCores ? 1 : 0.5, cursor: hasCores ? 'pointer' : 'not-allowed' }}>
              <SwitchLabelText style={{ cursor: 'inherit' }}>Cores</SwitchLabelText>
              <SwitchBox style={{ cursor: 'inherit' }}>
                <SwitchInput 
                  type="checkbox" 
                  checked={hasCores && selectedSections.cores} 
                  onChange={() => hasCores && toggleSection('cores')} 
                  disabled={!hasCores}
                />
                <SwitchSlider className="slider" />
              </SwitchBox>
            </SwitchRow>

            <SwitchRow style={{ opacity: hasComposicao ? 1 : 0.5, cursor: hasComposicao ? 'pointer' : 'not-allowed' }}>
              <SwitchLabelText style={{ cursor: 'inherit' }}>Composição</SwitchLabelText>
              <SwitchBox style={{ cursor: 'inherit' }}>
                <SwitchInput 
                  type="checkbox" 
                  checked={hasComposicao && selectedSections.composicao} 
                  onChange={() => hasComposicao && toggleSection('composicao')} 
                  disabled={!hasComposicao}
                />
                <SwitchSlider className="slider" />
              </SwitchBox>
            </SwitchRow>

            <SwitchRow style={{ opacity: hasAnotacoes ? 1 : 0.5, cursor: hasAnotacoes ? 'pointer' : 'not-allowed' }}>
              <SwitchLabelText style={{ cursor: 'inherit' }}>Anotações</SwitchLabelText>
              <SwitchBox style={{ cursor: 'inherit' }}>
                <SwitchInput 
                  type="checkbox" 
                  checked={hasAnotacoes && selectedSections.anotacoes} 
                  onChange={() => hasAnotacoes && toggleSection('anotacoes')} 
                  disabled={!hasAnotacoes}
                />
                <SwitchSlider className="slider" />
              </SwitchBox>
            </SwitchRow>

            <SwitchRow style={{ opacity: hasAprovacoes ? 1 : 0.5, cursor: hasAprovacoes ? 'pointer' : 'not-allowed' }}>
              <SwitchLabelText style={{ cursor: 'inherit' }}>Aprovações</SwitchLabelText>
              <SwitchBox style={{ cursor: 'inherit' }}>
                <SwitchInput 
                  type="checkbox" 
                  checked={hasAprovacoes && selectedSections.aprovacoes} 
                  onChange={() => hasAprovacoes && toggleSection('aprovacoes')} 
                  disabled={!hasAprovacoes}
                />
                <SwitchSlider className="slider" />
              </SwitchBox>
            </SwitchRow>

            <SwitchRow style={{ opacity: hasMedidas ? 1 : 0.5, cursor: hasMedidas ? 'pointer' : 'not-allowed' }}>
              <SwitchLabelText style={{ cursor: 'inherit' }}>Medidas</SwitchLabelText>
              <SwitchBox style={{ cursor: 'inherit' }}>
                <SwitchInput 
                  type="checkbox" 
                  checked={hasMedidas && selectedSections.medidas} 
                  onChange={() => hasMedidas && toggleSection('medidas')} 
                  disabled={!hasMedidas}
                />
                <SwitchSlider className="slider" />
              </SwitchBox>
            </SwitchRow>
          </SwitchesContainer>
          
          <div style={{ marginTop: '2rem' }}>
            {productData ? (
              <PDFDownloadLink
                document={<TechnicalSheetPDF data={productData} sections={selectedSections} gradesByType={gradesByType} gradeTypes={gradeTypes} coverImageUrl={base64Cover} attachmentImages={base64Images} extraData={extraData} compositionItems={compositionItems} liveObservations={liveObservations} liveAnotacoes={convertedAnotacoes} liveAprovacoes={convertedAprovacoes.length > 0 ? convertedAprovacoes : liveAprovacoes} liveCores={liveCores} liveMedidas={liveMedidas} />}
                fileName={`Ficha_Tecnica_${productData.productCode || 'Produto'}.pdf`}
                style={{ textDecoration: 'none' }}
              >
                {({ loading }) => (
                  <Button 
                    disabled={loading || isConvertingImages} 
                    onClick={() => {
                      if (!loading && !isConvertingImages) {
                        setTimeout(() => onClickClose(), 800)
                      }
                    }}
                    style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                  >
                    <Download size={18} style={{ marginRight: '8px' }} />
                    {isConvertingImages ? 'Preparando imagens...' : loading ? 'Gerando documento...' : 'Gerar e Baixar PDF'}
                  </Button>
                )}
              </PDFDownloadLink>
            ) : (
              <Button disabled style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <Download size={18} style={{ marginRight: '8px' }} />
                Carregando dados...
              </Button>
            )}
          </div>
        </div>
      </ContainerOfModal>
    </ModalTest>
  )
}

export default GeneratePDFModal
